import { useState, useEffect, useRef } from "react";
import { useRoute, useLocation } from "wouter";
import { Phone, MapPin, Package, Check, Clock, Loader2, ChevronDown, ChevronUp, AlertTriangle, Truck, type LucideIcon } from "lucide-react";

function pence(p: number) { return `£${(p / 100).toFixed(2)}`; }

const TERMINAL_STATUSES = new Set([
  "completed", "partially_completed", "cancelled", "failed",
]);

const STATUS_INFO: Record<string, { label: string; description: string; icon: LucideIcon }> = {
  awaiting_store_responses:  { label: "Order received",          description: "Waiting for stores to accept your order",              icon: Package },
  partially_accepted:        { label: "Partly accepted",         description: "One store has accepted — waiting for the other",       icon: Package },
  confirmed:                 { label: "Order confirmed",          description: "All stores have accepted your order",                 icon: Check },
  searching_driver:          { label: "Finding a driver",         description: "Looking for a driver to pick up your orders",          icon: Package },
  driver_assigned:           { label: "Driver assigned",          description: "Your driver is on the way to collect",                 icon: Package },
  coordinating_pickups:      { label: "Driver on the way",        description: "Your driver is heading to the first store",            icon: Package },
  first_pickup:              { label: "Collecting order 1",       description: "Driver is picking up your first order",                icon: Package },
  second_pickup:             { label: "Collecting order 2",       description: "Driver is picking up your second order",              icon: Package },
  third_pickup:              { label: "Collecting order 3",       description: "Driver is picking up your third order",               icon: Package },
  out_for_delivery:          { label: "On the way to you",        description: "All orders collected — your driver is heading to you", icon: Truck },
  completed:                 { label: "Delivered!",               description: "Your order has been delivered — enjoy!",               icon: Check },
  partially_completed:       { label: "Partially delivered",      description: "Part of your order has been delivered",               icon: Package },
  split_delivery:            { label: "Split delivery in progress", description: "Your orders are continuing as separate deliveries",   icon: Package },
  cancelled:                 { label: "Cancelled",                description: "Your combined order was cancelled",                   icon: Package },
  failed:                    { label: "Order failed",             description: "Something went wrong with your order",                icon: Package },
};

const CHILD_STATUS_LABELS: Record<string, string> = {
  pending:    "Waiting for store",
  accepted:   "Store accepted",
  preparing:  "Preparing your order",
  ready:      "Ready for collection",
  rejected:   "Declined",
  decoupled:  "Separated",
  completed:  "Delivered",
  cancelled:  "Cancelled",
};

type MultiOrderData = {
  id: number;
  status: string;
  totalPence: number;
  refundedPence?: number | null;
  splitDelivery?: boolean;
  deliveryAddress?: string | null;
  createdAt: string;
  completedAt?: string | null;
  cancelledAt?: string | null;
  pickupSequence?: Array<{ orderId: number; storeName: string; storeAddress?: string | null; role: string }> | null;
  driver?: { id: number; name: string; phone: string } | null;
  children?: Array<{
    orderId: number;
    role: string;
    childStatus: string;
    storeName: string;
    storeCategory?: string | null;
    subtotalPence: number;
  customerTotalPence?: number | null;
    refundedPence?: number | null;
  }> | null;
};

function ChildStatusBadge({ status }: { status: string }) {
  const label = CHILD_STATUS_LABELS[status] ?? status;
  const colours: Record<string, string> = {
    pending:   "bg-slate-100 text-slate-600",
    accepted:  "bg-blue-100 text-blue-700",
    ready:     "bg-amber-100 text-amber-700",
    rejected:  "bg-red-100 text-red-700",
    decoupled: "bg-slate-100 text-slate-500",
    completed: "bg-emerald-100 text-emerald-700",
    cancelled: "bg-red-100 text-red-600",
  };
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${colours[status] ?? "bg-slate-100 text-slate-600"}`}>
      {label}
    </span>
  );
}

export default function MultiOrderTracking() {
  const [, params] = useRoute("/order/multi-track/:multiOrderId");
  const [, navigate] = useLocation();
  const multiOrderId = params?.multiOrderId ?? "";

  const [data, setData] = useState<MultiOrderData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPickup, setShowPickup] = useState(true);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!multiOrderId) return;
    void fetchStatus();
    pollRef.current = setInterval(() => void fetchStatus(), 10000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [multiOrderId]);

  async function fetchStatus() {
    try {
      const token = localStorage.getItem("ayrshire_token");
      const headers: Record<string, string> = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const res = await fetch(`/api/multi-order/${multiOrderId}`, { headers });
      if (!res.ok) { setError("Could not load your order status."); setLoading(false); return; }
      const d = await res.json() as MultiOrderData;
      setData(d);
      setLoading(false);
      if (TERMINAL_STATUSES.has(d.status) && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    } catch {
      setLoading(false);
      setError("Network error loading order status.");
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={32} className="animate-spin text-primary mx-auto mb-3" />
          <p className="text-slate-500 text-sm">Loading your order…</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="max-w-sm w-full bg-white rounded-2xl border border-slate-200 p-8 text-center">
          <AlertTriangle size={36} className="mx-auto text-amber-500 mb-3" />
          <p className="font-semibold text-slate-700 mb-1">Couldn't load order</p>
          <p className="text-sm text-slate-400 mb-4">{error ?? "Unknown error"}</p>
          <button onClick={() => void fetchStatus()} className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90">
            Try again
          </button>
        </div>
      </div>
    );
  }

  const statusInfo = STATUS_INFO[data.status] ?? { label: data.status, description: "", icon: Package };
  const isTerminal = TERMINAL_STATUSES.has(data.status);
  const isCancelled = data.status === "cancelled" || data.status === "failed";
  const isComplete = data.status === "completed" || data.status === "partially_completed";
  const refundNotice = (data.refundedPence ?? 0) > 0
    ? `Recorded refund: ${pence(data.refundedPence!)}. Your bank may take time to show a completed refund.`
    : "No completed refund is recorded yet. Contact support if you need help.";

  // Progress steps for visual bar
  const PROGRESS_STEPS = [
    "awaiting_store_responses",
    "confirmed",
    "driver_assigned",
    "first_pickup",
    "second_pickup",
    "third_pickup",
    "out_for_delivery",
    "completed",
  ];
  const stepIdx = PROGRESS_STEPS.findIndex(s => s === data.status);
  const progressPct = isCancelled ? 0 : isComplete ? 100 : stepIdx >= 0 ? Math.round((stepIdx / (PROGRESS_STEPS.length - 1)) * 100) : 10;

  return (
    <div className="min-h-screen bg-white">
      {/* Status header */}
      <div className="bg-white text-[#1e3a5f] border-b border-slate-100">
        <div className="max-w-2xl mx-auto px-4 pt-8 pb-6">
          <div className="flex justify-center mb-3 text-primary"><statusInfo.icon size={32} aria-hidden /></div>
          <h1 className="text-[34px] leading-tight tracking-[-1.2px] font-bold mb-3">{statusInfo.label}</h1>
          <p className="text-sm opacity-80">{statusInfo.description}</p>

          {/* Progress bar */}
          {!isCancelled && (
            <div className="mt-5">
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#f0a500] rounded-full transition-all duration-700"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-5 py-8 space-y-4">
        {/* Driver card */}
        {data.driver && !isCancelled && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Package size={20} aria-hidden />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-slate-400 font-medium">Your driver</p>
              <p className="font-bold text-slate-800">{data.driver.name}</p>
            </div>
            {data.driver.phone && (
              <a
                href={`tel:${data.driver.phone}`}
                className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl px-4 py-2.5 text-sm font-semibold hover:bg-emerald-100 transition-colors"
              >
                <Phone size={15} />Call
              </a>
            )}
          </div>
        )}

        {/* Pickup sequence */}
        {data.pickupSequence && data.pickupSequence.length > 0 && !isCancelled && (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <button
              onClick={() => setShowPickup(o => !o)}
              className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
            >
              <span className="font-bold text-slate-800 text-sm">Collection route</span>
              {showPickup ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
            </button>
            {showPickup && (
              <div className="px-5 pb-5">
                <div className="relative">
                  {data.pickupSequence.map((stop, i) => (
                    <div key={stop.orderId} className="flex gap-4 mb-4 last:mb-0">
                      {/* Timeline line + dot */}
                      <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                          i === 0 ? "bg-primary border-primary text-white" : "bg-slate-100 border-slate-300 text-slate-600"
                        }`}>
                          {i + 1}
                        </div>
                        {i < (data.pickupSequence?.length ?? 0) - 1 && (
                          <div className="w-0.5 h-6 bg-slate-200 mt-1" />
                        )}
                      </div>
                      <div className="pt-1 min-w-0">
                        <p className="font-semibold text-sm text-slate-800">{stop.storeName}</p>
                        {stop.storeAddress && (
                          <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                            <MapPin size={10} />{stop.storeAddress}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                  {/* Delivery destination */}
                  {data.deliveryAddress && (
                    <div className="flex gap-4 mt-4 border-t border-dashed border-slate-200 pt-4">
                      <div className="flex flex-col items-center">
                        <div className="w-8 h-8 rounded-full bg-emerald-500 border-2 border-emerald-500 flex items-center justify-center flex-shrink-0">
                          <Check size={14} className="text-white" />
                        </div>
                      </div>
                      <div className="pt-1">
                        <p className="font-semibold text-sm text-slate-800">Your address</p>
                        <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                          <MapPin size={10} />{data.deliveryAddress}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Per-store status cards */}
        {data.children && data.children.length > 0 && (
          <div className="space-y-3">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide px-1">Store status</p>
            {data.children.map((child) => (
              <div key={child.orderId} className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <Package size={18} className="text-slate-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-slate-800 truncate">{child.storeName}</p>
                  {child.storeCategory && <p className="text-xs text-slate-400">{child.storeCategory}</p>}
                </div>
                <div className="flex flex-col items-end gap-1">
                  <ChildStatusBadge status={child.childStatus} />
                  {child.customerTotalPence != null && <span className="text-xs text-slate-600">Order total {pence(child.customerTotalPence)}</span>}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Payment summary */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-2">
          <h3 className="font-bold text-slate-800 text-sm mb-3">Payment</h3>
          <div className="flex justify-between text-sm text-slate-600">
            <span>Total paid</span>
            <span className="font-semibold text-slate-800">{pence(data.totalPence)}</span>
          </div>
          {(data.refundedPence ?? 0) > 0 && (
            <div className="flex justify-between text-sm text-emerald-600">
              <span>Recorded refunds</span>
              <span className="font-semibold">−{pence(data.refundedPence!)}</span>
            </div>
          )}
          <div className="flex items-center gap-2 pt-1">
            <div className={`w-2 h-2 rounded-full ${isCancelled ? "bg-red-400" : "bg-emerald-400"}`} />
            <p className="text-xs text-slate-400">
              {isCancelled ? refundNotice : "Paid securely via Stripe"}
            </p>
          </div>
        </div>

        {/* Timing */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-2 text-sm text-slate-500">
          <div className="flex items-center gap-2">
            <Clock size={14} className="text-slate-400" />
            <span>Ordered {new Date(data.createdAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</span>
          </div>
          {data.completedAt && (
            <div className="flex items-center gap-2">
              <Check size={14} className="text-emerald-500" />
              <span>Delivered {new Date(data.completedAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 pb-6">
          <button
            onClick={() => navigate("/order")}
            className="flex-1 py-3 rounded-xl border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-100 transition-colors"
          >
            Order more
          </button>
          <button
            onClick={() => navigate("/my-orders")}
            className="flex-1 py-3 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors"
          >
            My orders
          </button>
        </div>
      </div>
    </div>
  );
}
